import React from 'react';
import { LABEL_SOURCE_STYLES } from '../../constants';
import { EvalGtSample, LABEL_SOURCE_LABELS } from '../../types';
import { fmtIso } from '../../utils';
import { SampleStatusBadges } from './SampleStatusBadges';

interface GtSampleCardProps {
  sample: EvalGtSample;
  onClick?: () => void;
  /** Selection highlight (picker grids). */
  selected?: boolean;
  /** Grey out and block onClick (e.g. sample not ready to be picked). */
  disabled?: boolean;
  showSampleId?: boolean;
  /** Resolved dataset name shown under the meta lines (when assigned). */
  datasetName?: string | null;
  showIngestionInfo?: boolean;
  showStatusBadges?: boolean;
  /** Action buttons rendered next to the label-source badge. Callers are
   * responsible for stopPropagation when the card itself is clickable. */
  actions?: React.ReactNode;
}

/** Thumbnail card for a GT sample, shared by the browser/dataset/picker grids. */
export const GtSampleCard: React.FC<GtSampleCardProps> = ({
  sample,
  onClick,
  selected,
  disabled,
  showSampleId,
  datasetName,
  showIngestionInfo,
  showStatusBadges,
  actions,
}) => (
  <div
    className={`eval-inference-thumb${selected ? ' selected' : ''}`}
    onClick={disabled ? undefined : onClick}
    style={{
      cursor: disabled ? 'not-allowed' : onClick ? 'pointer' : undefined,
      opacity: disabled ? 0.5 : 1,
      outline: selected ? '2px solid var(--accent)' : undefined,
    }}
  >
    <div className="eval-inference-thumb-img">
      {sample.image_url ? (
        <img
          src={sample.image_url}
          alt={`image_id: ${sample.image_id}`}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ) : sample.image_id != null ? (
        `image_id: ${sample.image_id}`
      ) : (
        sample.gt_sample_id
      )}
    </div>
    <div className="eval-inference-thumb-meta">
      {showSampleId && (
        <div
          style={{
            marginBottom: 2,
            fontFamily: 'var(--font-mono)',
            fontSize: 10,
            color: 'var(--text-tertiary)',
          }}
        >
          {sample.gt_sample_id}
        </div>
      )}
      <div style={{ marginBottom: 2, fontFamily: 'var(--font-mono)', fontSize: 11 }}>
        store: {sample.store_id ?? '—'} · chain: {sample.chain_id ?? '—'}
      </div>
      {showIngestionInfo && (
        <>
          <div style={{ fontSize: 10, color: 'var(--text-tertiary)', marginBottom: 2 }}>
            {fmtIso(sample.ingested_at)}
          </div>
          {sample.ingested_by && (
            <div style={{ fontSize: 10, color: 'var(--text-secondary)', marginBottom: 2 }}>
              by {sample.ingested_by.split('@')[0]}
            </div>
          )}
        </>
      )}
      {datasetName && (
        <div
          style={{
            fontSize: 10,
            color: 'var(--text-tertiary)',
            marginBottom: 2,
            fontFamily: 'var(--font-mono)',
          }}
        >
          {datasetName}
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={LABEL_SOURCE_STYLES[sample.label_source]}>
          {LABEL_SOURCE_LABELS[sample.label_source]}
        </span>
        {actions && <span style={{ display: 'inline-flex', gap: 4 }}>{actions}</span>}
      </div>
      {showStatusBadges && <SampleStatusBadges sample={sample} />}
    </div>
  </div>
);
